"use client"

import {
  Paper,
  Badge,
  Group,
  Text,
  Button,
  Table,
  Collapsible,
  TbEye,
  CountryFlag,
  ScrollArea,
} from "@forestsync/ui";
import { TbTrees } from "@forestsync/ui";
import { useTranslations } from "next-intl";
import type { User } from "@/types/glassroom";
import { getCountryCode, getCountryName } from "@forestsync/db";
import Link from "next/link";
import { getOrganizationTypeBadge } from "@forestsync/lib/type-utils";

interface ProjectDeveloperTableProps {
  developers: User[];
}

export function ProjectDeveloperTable({
  developers,
}: ProjectDeveloperTableProps) {
  const t = useTranslations();

  const groupedByCountry = developers.reduce(
    (groups, dev) => {
      // Normalize the key so mixed-format DB values (e.g. "SE" vs "Sweden")
      // group under a single country node instead of splitting the tree.
      const country = getCountryName(getCountryCode(dev.country));
      if (!groups[country]) groups[country] = [];
      groups[country].push(dev);
      return groups;
    },
    {} as Record<string, User[]>,
  );

  if (developers.length === 0) {
    return (
      <Paper withBorder p="xl" radius="md">
        <Text className="text-center text-gray-500 dark:text-gray-400">
          {t("glassroom.noDevelopersFound")}
        </Text>
      </Paper>
    );
  }

  return (
    <Paper withBorder p={{ base: "xs", sm: "md" }} radius="md">
      <div className="space-y-2">
        {Object.entries(groupedByCountry)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([country, devs]) => (
            <Collapsible
              key={country}
              label={getCountryName(getCountryCode(country))}
              count={devs.length}
              icon={<CountryFlag countryCode={getCountryCode(country)} />}
              depth={0}
              color="green"
            >
              <ScrollArea ml={{ base: "0.5rem", sm: "1rem" }}>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t("common.name")}</Table.Th>
                      <Table.Th>
                        {t("glassroom.table.organizationType")}
                      </Table.Th>
                      <Table.Th>{t("glassroom.table.projectsOwned")}</Table.Th>
                      <Table.Th>{t("glassroom.table.totalArea")}</Table.Th>
                      <Table.Th>{t("glassroom.table.totalCredits")}</Table.Th>
                      <Table.Th>{t("glassroom.table.contactPerson")}</Table.Th>
                      <Table.Th>{t("common.actions")}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {devs.map((dev) => {
                      const orgType = getOrganizationTypeBadge(dev.type, t);
                      return (
                        <Table.Tr key={dev.refId}>
                          <Table.Td>
                            <div>
                              <Text fw={500} size="sm">
                                {dev.name}
                              </Text>
                              <Text
                                size="xs"
                                className="text-gray-500 font-mono"
                              >
                                {dev.refId}
                              </Text>
                            </div>
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              color={orgType.color}
                              variant="light"
                              size="sm"
                            >
                              {orgType.label}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Group gap="xs" align="center">
                              <TbTrees size={16} className="text-green-600" />
                              <Text size="sm" fw={500}>
                                {dev.forestsOwned || 0}
                              </Text>
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" fw={500}>
                              {(dev.totalAreaHa || 0).toLocaleString()}{" "}
                              {t("common.ha")}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" fw={500}>
                              {(dev.creditsIssued || 0).toLocaleString()}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text
                              size="sm"
                              className="text-gray-600 dark:text-gray-400"
                            >
                              {dev.contactInfo?.name || t("common.noData")}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Button
                              variant="light"
                              size="xs"
                              leftSection={<TbEye size={14} />}
                              component={Link}
                              href={`/developer/${dev.refId}`}
                            >
                              {t("common.view")}
                            </Button>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Collapsible>
          ))}
      </div>
    </Paper>
  );
}
